// src/pages/admin/GlobalDefaultTemplate.js
import React, { useEffect, useState } from "react";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import { Save } from "lucide-react";
import api from "../../api/axios";
import { formatCurrency, sanitizeNumberInput } from "../../utils/format";
import FloatingNumber from "../../components/input/FloatingNumber";

// Simple Toast Component
function Toast({ message, visible }) {
  if (!visible) return null;
  return (
    <div className="fixed bottom-6 right-6 bg-green-600 text-white px-4 py-2 rounded shadow-lg z-50">
      {message}
    </div>
  );
}

const OPTIONS = [
  "Seller Costs",
  "Short Sale",
  "FHA/VA/USDA Loans",
  "Buyer Costs",
  "Refinance Costs",
  "Conventional Costs",
];

const TEMPLATE_KEY_MAP = {
  "Seller Costs": ["seller_closing_costs_section1", "seller_closing_costs_section2"],
  "Short Sale": ["shortsale_closing_costs_section1", "shortsale_closing_costs_section2"],
  "FHA/VA/USDA Loans": ["fha_closing_costs", "va_closing_costs", "usda_closing_costs"],
  "Buyer Costs": [
    "buyer_tenure_section",
    "buyer_taxes_insurance_section",
    "buyer_prepaid_section",
    "buyer_payments_section",
    "buyer_closing_costs_section",
  ],
  "Refinance Costs": ["refinance_closing_costs_section1", "refinance_closing_costs_section2"],
  "Conventional Costs": ["conventional_adjustable_section", "conventional_closing_costs_section"],
};

export default function AdminGlobalDefaultTemplate() {
  const [activeOption, setActiveOption] = useState(OPTIONS[0]);
  const [template, setTemplate] = useState(null);
  const [toast, setToast] = useState({ message: "", visible: false });

  /** ----------------- Add / Remove Fee Row ----------------- */
  const addFeeRow = (sectionKey, leth) => {
    setTemplate((prev) => {
      const updated = { ...prev };
      const cnt = leth + 1;
      const newRow = {
        sr_no: cnt,
        key: "Other" + cnt,
        label: "Other",
        value: "0.00",
        type: "Flat Fee",
        unit: "$",
        rate: 0,
        Cash: "0.00",
        total: "0.00",
        lock: "N",
        direction: "CHARGE",
      };
      updated[sectionKey] = [...updated[sectionKey], newRow];
      return updated;
    });
  };

  const removeFeeRow = (sectionKey, idx) => {
    setTemplate((prev) => {
      const updated = { ...prev };
      updated[sectionKey] = updated[sectionKey].filter((_, i) => i !== idx);
      return updated;
    });
  };

  const updateFee = (sectionKey, idx, key, value) => {
    setTemplate((prev) => {
      const updated = { ...prev };
      updated[sectionKey][idx] = { ...updated[sectionKey][idx], [key]: value };
      return updated;
    });
  };



  /** ----------------- Update Disclaimer ----------------- */
  const updateDisclaimer = (optionKey, value) => {
    setTemplate((prev) => {
      const updated = { ...prev };
      updated.extra[optionKey] = {
        ...updated.extra[optionKey],
        disclaimer_message: value,
      };
      return updated;
    });
  };

  const saveDisclaimer = async (optionKey) => {
    try {
      const resp = await api.put(`/admin/global-template/disclaimer/${optionKey}`, {
        disclaimer_message: template.extra[optionKey].disclaimer_message,
      });
      if (resp.data.status === "success") {
        setToast({ message: "Disclaimer saved successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Error saving disclaimer.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    }
  };

  /** ----------------- Validation ----------------- */
  const validateSection = (sectionKey) => {
    const fees = template[sectionKey];
    if (!fees) return false;

    for (let i = 0; i < fees.length; i++) {
      const fee = fees[i];
      if (!fee.label || fee.label.trim() === "") {
        setToast({ message: `Label required in ${sectionKey} (Row ${i + 1})`, visible: true });
        return false;
      }
      // normalize: remove commas, trim spaces
      // (?? not || — fee.value can legitimately be the number 0, which is falsy
      // but not "missing"; || would coerce it to "" and fail validation below)
      const rawValue = String(fee.value ?? "").replace(/,/g, "").trim();

      if (rawValue === "" || isNaN(rawValue) || parseFloat(rawValue) < 0) {
        setToast({ message: `Amount must be > 0 in ${sectionKey} (Row ${i + 1})`, visible: true });
        return false;
      }
    }
    return true;
  };

  /** ----------------- Save Section ----------------- */
  const saveSection = async (sectionKey) => {
    if (!validateSection(sectionKey)) return;

    try {
      const resp = await api.put(`/admin/global-template/section/${sectionKey}`, {
        [sectionKey]: template[sectionKey],
      });
      if (resp.data.status === "success") {
        setToast({ message: "Section saved successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Error saving section.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    }
  };

  /** ----------------- Load Template ----------------- */
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const resp = await api.get("/admin/global-template");
        if (resp.data.status === "success" && resp.data.data.length > 0) {
          setTemplate(resp.data.data[0]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadTemplate();
  }, []);

  if (!template) return <div>Loading...</div>;

  const sectionKeys = TEMPLATE_KEY_MAP[activeOption];

  return (
    <div className="p-4 space-y-4">
      <AdminPageHeader title="Global Default Template" />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {OPTIONS.map((opt) => (
          <button
            key={opt}
            onClick={() => setActiveOption(opt)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition ${activeOption === opt
              ? "bg-blue-600 text-white shadow"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Render sections dynamically */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sectionKeys.map((sectionKey) => (
          <div
            key={sectionKey}
            className={`bg-white shadow-md rounded-xl p-4 border
              ${sectionKey === "buyer_closing_costs_section" ||
                sectionKey === "seller_closing_costs_section2" ||
                sectionKey === "seller_closing_costs_section1" ||
                sectionKey === "shortsale_closing_costs_section2" ||
                sectionKey === "shortsale_closing_costs_section1" ||
                sectionKey === "refinance_closing_costs_section1" ||
                sectionKey === "refinance_closing_costs_section2"
                ? "md:col-span-2"
                : "md:col-span-1"
              }
            `}
          >
            {/* Section Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-800 text-base">
                {sectionKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </h3>
              <button
                className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
                onClick={() => saveSection(sectionKey)}
              >
                <Save size={16} /> Save
              </button>
            </div>

            {/* Fees Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-auto border-collapse">
                <thead className="bg-gray-100 text-gray-700 uppercase">
                  <tr>
                    <th className="px-2 py-1 text-left border">Label</th>
                    {[
                      "seller_closing_costs_section2",
                      "shortsale_closing_costs_section2",
                      "buyer_closing_costs_section",
                      "refinance_closing_costs_section2",
                    ].includes(sectionKey) && (
                        <th className="px-2 py-1 text-left border w-[100px]">Type</th>
                      )}

                    <th className="px-2 py-1 text-center border w-[80px]">Amount</th>

                    {[
                      "fha_closing_costs",
                      "usda_closing_costs",
                      "va_closing_costs",
                    ].includes(sectionKey) && (
                        <th className="px-2 py-1 text-left border w-[100px]">Payer</th>
                      )}
                    {(sectionKey === "buyer_closing_costs_section" ||
                      sectionKey === "buyer_payments_section") && (
                        <th className="px-2 py-1 text-center border w-[80px]">Apply to Cash</th>
                      )}
                    {(sectionKey === "buyer_closing_costs_section" ||
                      sectionKey === "seller_closing_costs_section2" ||
                      sectionKey === "shortsale_closing_costs_section2") && (
                        <th className="px-2 py-1 text-left border w-[80px]">Cash</th>
                      )}
                    <th className="px-2 py-1 text-center border w-[40px]">Lock</th>
                  </tr>
                </thead>
                <tbody>
                  {template[sectionKey].map((fee, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      {/* Label */}
                      <td className="px-2 py-1 border">
                        <div className="flex items-center justify-between gap-2">
                          <input
                            type="text"
                            value={fee.label || ""}
                            onChange={(e) => updateFee(sectionKey, idx, "label", e.target.value)}
                            className="flex-1 border rounded px-1 py-1 text-sm"
                          />
                          {["seller_closing_costs_section1", "shortsale_closing_costs_section1", "buyer_payments_section"].includes(sectionKey) &&
                            fee.rate !== undefined && (
                              <FloatingNumber
                                type="number"
                                value={fee.rate || "0"}
                                onChange={(e) => updateFee(sectionKey, idx, "rate", e.target.value)}
                                className="w-12 border rounded px-1 py-1 text-sm text-right"
                              />
                            )}

                          {(sectionKey === "seller_closing_costs_section2" || sectionKey === "shortsale_closing_costs_section2" || sectionKey === "buyer_closing_costs_section" || sectionKey === "refinance_closing_costs_section2") &&
                            idx >= 10 && (
                              <button
                                onClick={() => removeFeeRow(sectionKey, idx)}
                                className="text-red-500 text-xs hover:underline"
                              >
                                ✕
                              </button>
                            )}

                        </div>
                      </td>



                      {/* Type / Payer / Blank Column */}
                      {/* Type */}
                      {[
                        "seller_closing_costs_section2",
                        "shortsale_closing_costs_section2",
                        "buyer_closing_costs_section",
                        "refinance_closing_costs_section2",
                      ].includes(sectionKey) && fee.type !== undefined && (
                          <td className="px-2 py-1 border w-[100px]">
                            <select
                              value={fee.type || "Flat Fee"}
                              onChange={(e) => updateFee(sectionKey, idx, "type", e.target.value)}
                              className="w-27 border rounded px-1 py-1 text-sm"
                            >
                              <option>Flat Fee</option>
                              <option>% Sales Price</option>
                              <option>% Loan</option>
                            </select>
                          </td>
                        )}

                      {/* Amount */}
                      <td className="px-2 py-1 border w-[80px]">
                        <div className="flex items-center gap-2">
                          {[
                            "buyer_closing_costs_section",
                            "seller_closing_costs_section2",
                            "shortsale_closing_costs_section2",
                            "refinance_closing_costs_section2",
                          ].includes(sectionKey) && (
                              <button
                                type="button"
                                onClick={() =>
                                  updateFee(
                                    sectionKey,
                                    idx,
                                    "direction",
                                    fee.direction === "CREDIT" ? "CHARGE" : "CREDIT"
                                  )
                                }
                                title={
                                  fee.direction === "CREDIT"
                                    ? "Credit — subtracts from the total"
                                    : "Charge — adds to the total"
                                }
                                className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded border text-xs font-bold ${
                                  fee.direction === "CREDIT"
                                    ? "border-green-500 text-green-600 bg-green-50"
                                    : "border-gray-300 text-gray-500"
                                }`}
                              >
                                {fee.direction === "CREDIT" ? "−" : "+"}
                              </button>
                            )}
                          <span className="text-gray-500 text-sm whitespace-nowrap">{fee.unit}</span>
                          <FloatingNumber
                            type="text"
                            value={
                              fee.value_type === "rate" ||
                                fee.value_type === "month" ||
                                fee.value_type === "year"
                                ? fee.value ?? ""
                                : fee.value !== undefined && fee.value !== null
                                  ? fee.value
                                  : "0.00"
                            }
                            onChange={(e) => updateFee(sectionKey, idx, "value", sanitizeNumberInput(e.target.value))}
                            onBlur={(e) => {
                              if (
                                fee.value_type !== "rate" &&
                                fee.value_type !== "month" &&
                                fee.value_type !== "year"
                              ) {
                                updateFee(sectionKey, idx, "value", formatCurrency(e.target.value));
                              }
                            }} className="w-20 flex-1 border rounded px-1 py-1 text-sm"
                          />
                        </div>
                      </td>

                      {[
                        "fha_closing_costs",
                        "usda_closing_costs",
                        "va_closing_costs",
                      ].includes(sectionKey) && fee.payer !== undefined && fee.payer !== "factor" && fee.label !== "Origination Factor" && (
                          <td className="px-2 py-1 border w-[100px]">
                            <select
                              value={fee.payer || "Buyer"}
                              onChange={(e) => updateFee(sectionKey, idx, "payer", e.target.value)}
                              className="w-27 border rounded px-1 py-1 text-sm"
                            >
                              <option>Buyer</option>
                              <option>Seller</option>
                            </select>
                          </td>
                        )}

                      {[
                        "fha_closing_costs",
                        "usda_closing_costs",
                        "va_closing_costs",
                      ].includes(sectionKey) && fee.payer !== undefined && fee.payer === "factor" && (
                          <td className="px-2 py-1 border w-[100px]"></td>
                        )}


                      {/* Apply checkbox */}
                      {(sectionKey === "buyer_closing_costs_section" ||
                        sectionKey === "buyer_payments_section") && (
                          <td className="px-2 py-1 border text-center w-[120px]">
                            {(sectionKey === "buyer_closing_costs_section" || fee.apply !== undefined) && (
                              <input
                                type="checkbox"
                                checked={fee.apply === "Y"}
                                onChange={(e) =>
                                  updateFee(sectionKey, idx, "apply", e.target.checked ? "Y" : "N")
                                }
                              />
                            )}
                          </td>
                        )}

                      {(sectionKey === "buyer_closing_costs_section" ||
                        sectionKey === "seller_closing_costs_section2" ||
                        sectionKey === "shortsale_closing_costs_section2") && (
                          <td className="px-2 py-1 border text-center w-[80px]">
                            <FloatingNumber
                              type="text"
                              value={
                                fee.value_type === "rate" ||
                                  fee.value_type === "month" ||
                                  fee.value_type === "year"
                                  ? fee.Cash ?? ""
                                  : fee.Cash !== undefined && fee.Cash !== null
                                    ? fee.Cash
                                    : "0.00"
                              }
                              onChange={(e) => updateFee(sectionKey, idx, "Cash", sanitizeNumberInput(e.target.value))}
                              onBlur={(e) => {
                                if (
                                  fee.value_type !== "rate" &&
                                  fee.value_type !== "month" &&
                                  fee.value_type !== "year"
                                ) {
                                  updateFee(sectionKey, idx, "Cash", formatCurrency(e.target.value));
                                }
                              }}
                              className="w-20 border rounded px-1 py-1 text-sm"
                            />
                          </td>
                        )}

                      {/* Lock */}
                      <td className="px-2 py-1 text-center border w-[20px]">
                        <input
                          type="checkbox"
                          checked={fee.lock === "Y"}
                          onChange={(e) =>
                            updateFee(sectionKey, idx, "lock", e.target.checked ? "Y" : "N")
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>

                {/* Add Row button after 10 rows */}
                {(sectionKey === "seller_closing_costs_section2" || sectionKey === "shortsale_closing_costs_section2" || sectionKey === "buyer_closing_costs_section" || sectionKey === "refinance_closing_costs_section2") &&
                  template[sectionKey].length >= 10 && template[sectionKey].length < 15 && (
                    <tfoot>
                      <tr>
                        <td colSpan="100%" className="px-2 py-2 text-center border">
                          <button
                            onClick={() => addFeeRow(sectionKey, template[sectionKey].length)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                          >
                            + Add Row
                          </button>
                        </td>
                      </tr>
                    </tfoot>
                  )}
              </table>
            </div>

          </div>
        ))}
      </div>

      {/* Toast */}
      <Toast message={toast.message} visible={toast.visible} />

      {/* Disclaimer Editor */}
      {["Seller Costs", "Buyer Costs", "Short Sale", "Refinance Costs"].includes(activeOption) && (
        <div className="bg-white shadow-md rounded-xl p-4 border md:col-span-2">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-800 text-base">
              Disclaimer for {activeOption}
            </h3>
            <button
              className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
              onClick={() => saveDisclaimer(activeOption)}
            >
              <Save size={16} /> Save Disclaimer
            </button>
          </div>
          <textarea
            value={template.extra[activeOption]?.disclaimer_message || ""}
            onChange={(e) => updateDisclaimer(activeOption, e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
            rows={3}
          />
        </div>
      )}
    </div>
  );
}
